/**
 * Magic Link Verify Component
 *
 * ISO 27001 A.9.4.2 - Secure log-on procedures
 * Composant de vérification du token magic link (callback URL)
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Loader2,
  CheckCircle,
  XCircle,
  Mail,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { magicLinkApi } from '../api/magic-link.api';
import { useSessionContext } from '@/contexts/SessionContext';

export interface MagicLinkVerifyProps {
  /** URL de redirection après connexion réussie */
  redirectTo?: string;
  /** Classe CSS additionnelle */
  className?: string;
}

type VerifyState = 'verifying' | 'success' | 'error' | 'expired';

export const MagicLinkVerify: React.FC<MagicLinkVerifyProps> = ({
  redirectTo = '/dashboard',
  className = '',
}) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useSessionContext();

  const [state, setState] = useState<VerifyState>('verifying');
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setState('error');
      setError(t('auth.magicLink.noToken'));
      return;
    }

    const verifyToken = async () => {
      try {
        const result = await magicLinkApi.verifyMagicLink(token);

        if (result.success && result.accessToken && result.user) {
          // Store auth data via SessionContext
          // Signature: setSession(accessToken, refreshToken, expiresIn, user)
          setSession(
            result.accessToken,
            result.refreshToken || '',
            result.expiresIn || 900,
            result.user
          );

          setState('success');

          // Determine redirect URL based on user role
          // SUPERADMIN → direct to superadmin dashboard (bypass onboarding)
          let finalRedirectUrl = redirectTo;
          if (result.user.role === 'SUPERADMIN') {
            finalRedirectUrl = '/superadmin';
          }

          // Start countdown for redirect
          const interval = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(interval);
                navigate(finalRedirectUrl);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          return () => clearInterval(interval);
        } else {
          setState('error');
          setError(result.message || t('auth.magicLink.verificationError'));
        }
      } catch (err: any) {
        if (err.message?.includes('expiré') || err.message?.includes('expired')) {
          setState('expired');
        } else {
          setState('error');
        }
        setError(err.message || t('auth.magicLink.invalidOrExpired'));
      }
    };

    verifyToken();
  }, [token, setSession, navigate, redirectTo]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleRedirectNow = () => {
    navigate(redirectTo);
  };

  // Verifying state
  if (state === 'verifying') {
    return (
      <div className={`w-full max-w-md mx-auto ${className}`}>
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg shadow-teal-500/30">
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            {t('auth.magicLink.verifying')}
          </h2>

          <p className="text-gray-500">
            {t('auth.magicLink.verifyingDescription')}
          </p>

          <div className="mt-8 flex justify-center">
            <div className="flex gap-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-3 h-3 bg-teal-500 rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (state === 'success') {
    return (
      <div className={`w-full max-w-md mx-auto ${className}`}>
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg shadow-green-500/30">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            {t('auth.magicLink.success')}
          </h2>

          <p className="text-gray-500 mb-6">
            {t('auth.magicLink.redirecting', { seconds: countdown })}
          </p>

          <button
            onClick={handleRedirectNow}
            className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800
              text-white font-bold text-base rounded-2xl transition-all duration-300
              shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40
              flex items-center justify-center gap-3"
          >
            <span>{t('auth.magicLink.continueToDashboard')}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Expired state
  if (state === 'expired') {
    return (
      <div className={`w-full max-w-md mx-auto ${className}`}>
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
            <RefreshCw className="w-10 h-10 text-white" />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            {t('auth.magicLink.expired')}
          </h2>

          <p className="text-gray-500 mb-6">
            {t('auth.magicLink.expiredDescription')}
          </p>

          <button
            onClick={handleGoToLogin}
            className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800
              text-white font-bold text-base rounded-2xl transition-all duration-300
              shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40
              flex items-center justify-center gap-3"
          >
            <Mail className="w-5 h-5" />
            <span>{t('auth.magicLink.requestNew')}</span>
          </button>

          <p className="mt-4 text-sm text-gray-400">
            {t('auth.magicLink.redirectToLogin')}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className={`w-full max-w-md mx-auto ${className}`}>
      <div className="text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg shadow-red-500/30">
          <XCircle className="w-10 h-10 text-white" />
        </div>

        <h2 className="text-3xl font-bold text-gray-900 mb-3">
          {t('auth.magicLink.invalidLink')}
        </h2>

        <p className="text-gray-500 mb-2">
          {error || t('auth.magicLink.invalidLinkDescription')}
        </p>

        <p className="text-gray-400 text-sm mb-6">
          {t('auth.magicLink.invalidLinkHint')}
        </p>

        <button
          onClick={handleGoToLogin}
          className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800
            text-white font-bold text-base rounded-2xl transition-all duration-300
            shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40
            flex items-center justify-center gap-3"
        >
          <Mail className="w-5 h-5" />
          <span>{t('auth.magicLink.backToLogin')}</span>
        </button>
      </div>
    </div>
  );
};

export default MagicLinkVerify;
